import { z } from "zod";
import type { DocumentTypeConfig } from "../types";

export { isAllowedCountryCode } from "../rules";

/**
 * French residence permit types (titres de séjour).
 *
 * Acronyms:
 * - mtp: Métiers en tension / Passeport talent
 * - aps: Autorisation provisoire de séjour
 * - dceme: Document de circulation pour étranger mineur
 */
export const ResidencePermitTypeEnum = z.enum([
  "titre_sejour_temporaire",
  "carte_resident",
  "carte_sejour_pluriannuelle",
  "carte_competences_et_talents",
  "carte_membre_famille_ue",
  "carte_mtp",
  "carte_aps",
  "carte_recepissee",
  "titre_de_voyage",
  "titre_d_identite_republicain",
  "dceme",
  "titre_de_travail",
  "autre",
]);

export const ResidencePermitDataSchema = z.object({
  first_name: z.string(),
  last_name: z.string(),
  birth_date: z.string(),
  birth_place_city: z.string().optional(),
  birth_place_country_code: z.string().optional(),
  sex: z.enum(["male", "female", "neutral"]),
  nationality_country_code: z.string(),
  issuing_country_code: z.string(),
  issue_date: z.string(),
  expiration_date: z.string(),
  document_number: z.string(),
  is_student_status: z.boolean(),
  residence_permit_type: ResidencePermitTypeEnum,
  requires_original_permit: z.boolean(),
});

export type ResidencePermitData = z.infer<typeof ResidencePermitDataSchema>;

export const ResidencePermitExtractionSchema = z.object({
  isValidDocumentType: z.boolean(),
  reason: z.string().optional(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  birth_date: z.string().nullable(),
  birth_place_city: z.string().nullable(),
  birth_place_country_code: z.string().nullable(),
  sex: z.enum(["male", "female", "neutral"]).nullable(),
  nationality_country_code: z.string().nullable(),
  issuing_country_code: z.string().nullable(),
  issue_date: z.string().nullable(),
  expiration_date: z.string().nullable(),
  document_number: z.string().nullable(),
  is_student_status: z.boolean().nullable(),
  residence_permit_type: ResidencePermitTypeEnum.nullable(),
  requires_original_permit: z.boolean().nullable(),
});

export type ResidencePermitExtraction = z.infer<typeof ResidencePermitExtractionSchema>;

export const ResidencePermitContextSchema = z.object({
  talentId: z.guid(),
});

export type ResidencePermitContext = z.infer<typeof ResidencePermitContextSchema>;

type ResidencePermitConfig = DocumentTypeConfig<typeof ResidencePermitDataSchema, typeof ResidencePermitContextSchema>;

export const RESIDENCE_PERMIT_CONFIG: ResidencePermitConfig = {
  documentType: "residence_permit",
  documentName: "Titre de séjour",
  maxFiles: 5,
  acceptedMimeTypes: ["image/jpeg", "image/png", "application/pdf"],
  dataSchema: ResidencePermitDataSchema,
  contextSchema: ResidencePermitContextSchema,
  requiredFields: [
    "first_name",
    "last_name",
    "birth_date",
    "sex",
    "nationality_country_code",
    "issuing_country_code",
    "expiration_date",
    "issue_date",
    "document_number",
    "is_student_status",
    "residence_permit_type",
    "requires_original_permit",
  ],
  fieldLabels: {
    first_name: "Prénom",
    last_name: "Nom",
    birth_date: "Date de naissance",
    birth_place_city: "Ville de naissance",
    birth_place_country_code: "Pays de naissance",
    sex: "Sexe",
    nationality_country_code: "Nationalité",
    issuing_country_code: "Pays émetteur",
    issue_date: "Date de délivrance",
    expiration_date: "Date d'expiration",
    document_number: "Numéro de document",
    is_student_status: "Statut étudiant",
    residence_permit_type: "Type de titre de séjour",
    requires_original_permit: "Ce récépissé doit-il être accompagné du titre de séjour original ?",
  },
};
